import { useEffect, useState } from "react";
import { CPPageBody } from "@warden-ui/common-components";
import { CardContentSignIn } from "@warden-ui/card-components";
import { CupixWorksApi, WardenApi } from "@warden-ui/api";
import { 
  useAuth, 
  SignInMethod, 
  SignInMethodConfig, 
  SignInMethodKeys,
  parseQueryString,
  parseAmplifyConfig,
  Region,
  cloneSearchParams,
  containsEmailDomain,
  isEmailFormat,
  CupixWorksDomain,
  set
} from "@warden-ui/common";
import { useSearchParams, useNavigate} from "react-router-dom";
import { convertOAuthToWardenConfig, CupixWorksDomainPostfix, SignInWithAzure } from "@warden-ui/common";
import { useTranslation } from "react-i18next";

const defaultQueryString: {[key: string]: string} = {
  team_domain: "",
  region: "",
  config: "",
  context: "",
  skip_team: "false",
  clear: "true",
}

export default function SignIn({
  oauthFlow = false,
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const convertedWardenConfig = convertOAuthToWardenConfig(searchParams);
  searchParams.set("config", convertedWardenConfig ?? searchParams.get("config") ?? "");
  searchParams.set("oauth_flow", searchParams.get("oauth_flow") ?? oauthFlow.toString());
  if (oauthFlow && searchParams.get("state")) {
    searchParams.set("context", searchParams.get("state")!);
  }

  const queryString = parseQueryString(searchParams, defaultQueryString);
  const { t } = useTranslation();
  const amplifyConfig = parseAmplifyConfig(queryString);
  auth.configureAmplify(amplifyConfig);

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [region, setRegion] = useState(amplifyConfig.region);
  const [teamDomain, setTeamDomain] = useState("");
  const [teamInfo, setTeamInfo] = useState<{
    config: SignInMethodConfig[];
    name: string;
    emailDomain: string;
    signinMethods: string;
    emailSignin: boolean;
    useSelfSignup: boolean;
    showSSOSignIn: boolean;
    ssoForcedEmailDomains: string[];
  } | undefined>(undefined);

  useEffect(() => {
    console.info("Referrer", document.referrer);

    if (!queryString.skip_team && !queryString.team_domain) {
      navigate(`/teams/domain?${searchParams.toString()}`);
    }

    const cupixWorksApiEndpoint = amplifyConfig[region]?.cupixworksApiEndpoint;
    if (
      !queryString.skip_team &&
      cupixWorksApiEndpoint &&
      queryString.team_domain?.length > 0
    ) {
      new CupixWorksApi(cupixWorksApiEndpoint)
        .findTeamByDomain(queryString.team_domain)
        .then((data) => {
          const signInMethodConfig = data.sign_in_methods
            .filter((method: string) => method !== "email")
            .map((method: string) => {
              const [type, provider] = method.split(":");
              return {
                type: type as SignInMethod,
                identityProvider: provider || type,
                onClick: (identityProvider: string) => {
                  console.log("sign in with", identityProvider);
                  set('identity_provider', identityProvider);

                  if (identityProvider !== SignInMethodKeys.AAD) {
                    auth.federatedSignIn(identityProvider, amplifyConfig);
                  } else {
                    // TODO: remove this block after AAD is migrated to new API
                    SignInWithAzure(
                      cupixWorksApiEndpoint,
                      queryString.team_domain
                    );
                  }
                },
              } as SignInMethodConfig;
            });

          setTeamDomain(
            `${queryString.team_domain}${CupixWorksDomainPostfix(
              cupixWorksApiEndpoint
            )}`
          );
          setTeamInfo({
            config: signInMethodConfig,
            name: data.name,
            emailDomain: data.email_domain ?? "",
            signinMethods: data.sign_in_methods.join(","),
            emailSignin: data.sign_in_methods.includes("email"),
            useSelfSignup:
              "use_self_signup" in queryString
                ? queryString.use_self_signup
                : data.use_self_signup,
            showSSOSignIn: false,
            ssoForcedEmailDomains: data.sso_forced_email_domains,
          });
        });
    } else {
      // NOTE: entry point for app.cupix.works
      setTeamInfo({
        config: [],
        name: "CupixWorks",
        emailDomain: "",
        signinMethods: "email",
        emailSignin: true,
        useSelfSignup: false,
        showSSOSignIn: true,
        ssoForcedEmailDomains: []
      })
    }
  }, []);

  const configureFailed = () => {
    if (!auth.isConfigured) {
      console.error("Auth is not configured.");
      return true;
    }
    return false;
  }

  const onRegionChanged = (newRegion: Region) => {
    setRegion(newRegion);
  };

  const onClickEmailSignin = async (email: string) => {
    if (configureFailed()) {
      return;
    }

    if (email.length === 0) {
      setErrorMessage(t('errors.email_empty'));
      return;
    }

    if (!isEmailFormat(email)) {
      setErrorMessage(t('errors.email_invalid'));
      return;
    }

    if(containsEmailDomain(email, teamInfo?.ssoForcedEmailDomains)) {
      setErrorMessage(t('errors.email_signin_denied'));
      return;
    }

    setLoading(true);
    WardenApi.getUserInfo(email, { region: region })
      .then((data) => {
        const params = cloneSearchParams(searchParams);
        params.set("region", region);
        params.set("email", email);
        params.set("clear", "false");

        if (data.status === "CONFIRMED") {
          navigate(`/signin/password?${params.toString()}`);

        // NOTE: only if user is external provider without team_domain(via app.cupix.works), redirect to find team page
        } else if (data.status === "EXTERNAL_PROVIDER") {
          if (!queryString.team_domain) {
            params.delete("skip_team");

            navigate(`/teams/domain?${params.toString()}`);
          } else {
            setErrorMessage(t('signin.error.try_sso'));
          }
        } else {
          params.set("migration", "true");
          navigate(`/reset_password/request?${params.toString()}`);
        }
      })
      .catch((reason) => {
        if (teamInfo && teamInfo.config.length > 0) {
          setErrorMessage(t('signin.error.try_sso'));
        } else if (amplifyConfig.regions.length > 1) {
          setErrorMessage(t('signin.error.email_not_found_with_regions'));
        } else {
          setErrorMessage(t('signin.error.email_not_found'));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClickTeamDomain = () => {
    const params = cloneSearchParams(searchParams);
    params.delete("skip_team");
    params.set("region", region);
    params.set("clear", "false");

    navigate(`/teams/domain?${params.toString()}`);
  };

  const onClickJoinTeam = () => {
    window.location.href = `${CupixWorksDomain(
      queryString.team_domain,
      region
    )}/signup/join_req?team_domain=${queryString.team_domain}&join=true`;
  }

  if (auth.isSignedIn) {
    navigate(`/redirect?${searchParams.toString()}`);
  }

  return (
    <CPPageBody
      regions={amplifyConfig.regions}
      defaultRegion={amplifyConfig.region}
      showRegion={!queryString.team_domain && amplifyConfig.regions.length > 1}
      onRegionChanged={onRegionChanged}
    >
      <CardContentSignIn
        signInMethodConfig={teamInfo?.config}
        enabledEmailSignin={teamInfo?.emailSignin}
        onClickEmailSignin={onClickEmailSignin}
        onClickTeamDomain={onClickTeamDomain}
        email={queryString.email}
        teamName={teamInfo?.name}
        teamDomain={teamDomain}
        emailDomain={teamInfo?.emailDomain}
        teamSelect={!queryString.skip_team}
        selfSignup={teamInfo?.useSelfSignup}
        showSSOSignIn={teamInfo?.showSSOSignIn}
        onClickJoinTeam={onClickJoinTeam}
        errorMessage={errorMessage}
        loading={loading}
        skeletonLoading={teamInfo === undefined}
      />
    </CPPageBody>
  );
}
